import {useMemo, useRef, useState} from 'react';
import type {SelfEmploymentProvider} from '@rocket-ui/lk-api';
import {
	createEmptyDocumentsUploadValues,
	EMPTY_FOREIGN_DOCUMENT_DETAILS,
	getRegistrationDocumentsFlowKey,
	EMPTY_PASSPORT_DETAILS,
	EMPTY_PAYMENT_DETAILS,
	EMPTY_RESIDENT_CODE,
	EMPTY_SNILS,
	EMPTY_TAX_IDENTIFIER,
	extractIdentificationDocumentPhotos,
	MyTaxCheckingMode,
	MyTaxNameMismatchInfo,
	PaymentDetailsValues,
	PaymentSubStep,
	RegistrationPaymentMethod,
	RegistrationStep,
	RegistrationWorkStatus,
	isKazakhstanCitizenship,
	resolveDocumentsFlow,
	resolveKazakhIdentificationDocumentType,
	resolveRegistrationFlow,
	WORK_STATUS_STEP_ENABLED,
	type RegistrationBackendFlags,
	type SelfEmploymentCheckOptions,
	WorkStatusConnectionStates,
} from 'features/registration';
import {EMPTY_INN, EMPTY_PERSONAL_INFO} from './constants';
import type {IdentityEditSnapshot, MyTaxLoadErrorRetry} from './types';

export type RegistrationWizardEntry = 'statuses';

const isStatusesProfileEntry = (entry?: RegistrationWizardEntry): boolean => {
	return entry === 'statuses' && WORK_STATUS_STEP_ENABLED;
};

/** Весь стейт визарда регистрации и производные от него флоу документов и регистрации. */
export const useRegistrationWizardState = (entry?: RegistrationWizardEntry) => {
	const openedAtStatuses = isStatusesProfileEntry(entry);
	const [isSubmitting, setIsSubmitting] = useState(false);
	/** Какая кнопка dual-footer «Мой налог» сейчас грузится (чтобы лоадер был только на ней). */
	const [myTaxFooterLoading, setMyTaxFooterLoading] = useState<'goToSite' | 'confirm' | null>(null);
	const [step, setStep] = useState(openedAtStatuses ? RegistrationStep.WorkStatus : RegistrationStep.PersonalInfo);
	const [personalInfo, setPersonalInfo] = useState(EMPTY_PERSONAL_INFO);
	const [passportDetails, setPassportDetails] = useState(EMPTY_PASSPORT_DETAILS);
	const [foreignDocumentDetails, setForeignDocumentDetails] = useState(EMPTY_FOREIGN_DOCUMENT_DETAILS);
	const [pendingPaymentMethod, setPendingPaymentMethod] = useState<RegistrationPaymentMethod | null>(null);
	const [paymentSubStep, setPaymentSubStep] = useState<PaymentSubStep>('select');
	const [paymentDetails, setPaymentDetails] = useState<PaymentDetailsValues>(EMPTY_PAYMENT_DETAILS);
	const [workStatus, setWorkStatus] = useState<RegistrationWorkStatus | null>(null);
	/** `free_smz` — ИНН и bind_partner, без экрана «Нужен доступ в «Мой налог»». */
	const [selfEmploymentPartner, setSelfEmploymentPartner] = useState<SelfEmploymentProvider | null>(null);
	const [innStep, setInnStep] = useState(EMPTY_INN);
	const [taxIdentifierStep, setTaxIdentifierStep] = useState(EMPTY_TAX_IDENTIFIER);
	const [snilsStep, setSnilsStep] = useState(EMPTY_SNILS);
	const [residentCodeStep, setResidentCodeStep] = useState(EMPTY_RESIDENT_CODE);
	const [isHowToBecomeOpen, setIsHowToBecomeOpen] = useState(false);
	const [exitConfirmKind, setExitConfirmKind] = useState<'registration' | 'status' | null>(null);
	const [myTaxNameMismatch, setMyTaxNameMismatch] = useState<MyTaxNameMismatchInfo | null>(null);
	const [myTaxCheckingMode, setMyTaxCheckingMode] = useState<MyTaxCheckingMode>('status');
	const [myTaxCheckingOptions, setMyTaxCheckingOptions] = useState<Pick<SelfEmploymentCheckOptions, 'skipValidation'>>({});
	const [myTaxLoadErrorRetry, setMyTaxLoadErrorRetry] = useState<MyTaxLoadErrorRetry>('check');
	const [innError, setInnError] = useState<string | null>(null);
	const [hasInnSaved, setHasInnSaved] = useState(false);
	const [hasSnilsSaved, setHasSnilsSaved] = useState(false);
	/** ИНН в пагинации текущего подключения статуса (false — экран пропущен, ИНН уже был). */
	const [statusConnectionIncludesInn, setStatusConnectionIncludesInn] = useState(true);
	/** СНИЛС в пагинации текущего подключения статуса (false — экран пропущен, СНИЛС уже был). */
	const [statusConnectionIncludesSnils, setStatusConnectionIncludesSnils] = useState(true);
	const [backendFlags, setBackendFlags] = useState<RegistrationBackendFlags>({});
	const [workStatusConnections, setWorkStatusConnections] = useState<WorkStatusConnectionStates>({});
	/** Плитки «Выберите статус» — только после GET /employment, иначе мелькают пустые статусы. */
	const [workStatusesReady, setWorkStatusesReady] = useState(false);
	const [returnToIdentityReview, setReturnToIdentityReview] = useState(false);
	/** Вход с профиля (`?edit=` или `/profile/statuses`) — крестик возвращает в профиль. */
	const [openedFromProfileAtStatuses, setOpenedFromProfileAtStatuses] = useState(openedAtStatuses);
	/** Шаг, с которого открыли регистрацию из профиля: «Назад» с него возвращает в профиль. */
	const [profileEntryStep, setProfileEntryStep] = useState<RegistrationStep | null>(
		openedAtStatuses ? RegistrationStep.WorkStatus : null,
	);
	const [identityEditSnapshot, setIdentityEditSnapshot] = useState<IdentityEditSnapshot | null>(null);
	const [identificationDocumentPhotos, setIdentificationDocumentPhotos] = useState<
		ReturnType<typeof extractIdentificationDocumentPhotos>
	>(null);

	const documentsFlow = useMemo(
		() =>
			resolveDocumentsFlow({
				citizenshipOksmCode: personalInfo.citizenshipOksmCode,
				residenceOksmCode: personalInfo.residenceOksmCode,
				residenceCountryName: personalInfo.residenceCountryName,
				identificationDocumentType: isKazakhstanCitizenship(personalInfo.citizenshipOksmCode)
					? resolveKazakhIdentificationDocumentType(foreignDocumentDetails.documentType)
					: null,
			}),
		[
			foreignDocumentDetails.documentType,
			personalInfo.citizenshipOksmCode,
			personalInfo.residenceCountryName,
			personalInfo.residenceOksmCode,
		],
	);

	const documentsFlowKey = getRegistrationDocumentsFlowKey(
		personalInfo.citizenshipOksmCode,
		personalInfo.residenceOksmCode,
		foreignDocumentDetails.documentType,
	);
	/**
	 * Восстановление шага после перезагрузки выставляет этот ключ.
	 * Смена гражданства с пустого профиля не должна сбрасывать восстановленный шаг.
	 */
	const preserveDocumentsFlowKeyRef = useRef<string | null>(null);

	const [documentsUpload, setDocumentsUpload] = useState(() =>
		createEmptyDocumentsUploadValues(documentsFlow),
	);

	const registrationFlow = useMemo(() => {
		const flow = resolveRegistrationFlow({
			citizenshipOksmCode: personalInfo.citizenshipOksmCode,
			residenceOksmCode: personalInfo.residenceOksmCode,
			workStatus,
			backendFlags,
		});

		// free_smz: только ИНН, шаг «Введите номер СНИЛС» не показываем.
		if (selfEmploymentPartner === 'free_smz' && workStatus === 'self_employment') {
			return {...flow, snils: 'hidden' as const};
		}

		return flow;
	}, [
		backendFlags,
		personalInfo.citizenshipOksmCode,
		personalInfo.residenceOksmCode,
		selfEmploymentPartner,
		workStatus,
	]);

	const resetPaymentFlow = () => {
		setPendingPaymentMethod(null);
		setPaymentSubStep('select');
		setPaymentDetails(EMPTY_PAYMENT_DETAILS);
	};

	return {
		backendFlags,
		documentsFlow,
		documentsFlowKey,
		documentsUpload,
		exitConfirmKind,
		foreignDocumentDetails,
		hasInnSaved,
		hasSnilsSaved,
		identificationDocumentPhotos,
		identityEditSnapshot,
		innError,
		innStep,
		isHowToBecomeOpen,
		isSubmitting,
		myTaxCheckingMode,
		myTaxCheckingOptions,
		myTaxFooterLoading,
		myTaxLoadErrorRetry,
		myTaxNameMismatch,
		passportDetails,
		paymentDetails,
		paymentSubStep,
		pendingPaymentMethod,
		personalInfo,
		preserveDocumentsFlowKeyRef,
		registrationFlow,
		residentCodeStep,
		openedFromProfileAtStatuses,
		profileEntryStep,
		returnToIdentityReview,
		selfEmploymentPartner,
		snilsStep,
		statusConnectionIncludesInn,
		statusConnectionIncludesSnils,
		step,
		taxIdentifierStep,
		workStatus,
		workStatusConnections,
		workStatusesReady,
		resetPaymentFlow,
		setBackendFlags,
		setDocumentsUpload,
		setExitConfirmKind,
		setForeignDocumentDetails,
		setHasInnSaved,
		setHasSnilsSaved,
		setIdentificationDocumentPhotos,
		setIdentityEditSnapshot,
		setOpenedFromProfileAtStatuses,
		setProfileEntryStep,
		setInnError,
		setInnStep,
		setIsHowToBecomeOpen,
		setIsSubmitting,
		setMyTaxCheckingMode,
		setMyTaxCheckingOptions,
		setMyTaxFooterLoading,
		setMyTaxLoadErrorRetry,
		setMyTaxNameMismatch,
		setPassportDetails,
		setPaymentDetails,
		setPaymentSubStep,
		setPendingPaymentMethod,
		setPersonalInfo,
		setResidentCodeStep,
		setReturnToIdentityReview,
		setSelfEmploymentPartner,
		setSnilsStep,
		setStatusConnectionIncludesInn,
		setStatusConnectionIncludesSnils,
		setStep,
		setTaxIdentifierStep,
		setWorkStatus,
		setWorkStatusConnections,
		setWorkStatusesReady,
	};
};

export type RegistrationWizardState = ReturnType<typeof useRegistrationWizardState>;
